/*
  Paints the gold orb (snippets/chat-launcher.liquid) over Shopify's stock
  chat pill, and keeps it there.

  THE ACTUAL DOM, CONFIRMED AGAINST THE LIVE STOREFRONT
  Do not re-derive this from the page source; every part is inside shadow
  roots and none of it is visible to devtools' normal search:

    <shopify-chat>                     fixed, full viewport, z 2147483000
      #shopify-element-wrapper         (open shadow root)
        <shopify-agent-runtime>        (NESTED open shadow root)
          #shopify-element-wrapper
            .activator-nudge           <- the white pill, 85x46
              button.activator         <- Shopify's real button
            section.panel              <- the chat panel

  Facts that drive the design, all verified live:

  1. THE PILL IS TWO SHADOW ROOTS DEEP. Theme stylesheets cannot reach it.
     A <style> has to be placed inside each root individually.

  2. THE NODES ARE REPLACED AT RUNTIME. Before the first open, the
     activator is in the OUTER root. On open, that subtree is torn down and
     rebuilt inside <shopify-agent-runtime>. Cached element references go
     stale; styles attached to a ROOT survive, which is why the hide is
     done with injected stylesheets.

  3. THE ACTIVATOR COLLAPSES TO 0x0 WHILE THE PANEL IS OPEN. It is not
     moved or hidden by a class — its box genuinely becomes zero. So a
     zero-size measurement means "chat is open", not "measurement failed".
     That is the signal the orb uses to withdraw while the panel is up, and
     it is a size change rather than a DOM change, which is why the pill
     needs a ResizeObserver and not just a MutationObserver.

  WHY THE ORB DOES NOT HANDLE CLICKS
  Earlier versions made the orb a real button and forwarded the click to
  Shopify with element.click(), then with a full synthetic pointer
  sequence. Both were unreliable in the same way: the panel would open and
  instantly close again, so it took several clicks to stick. Cause: the
  synthetic events bubble to document, where Shopify's own click-outside
  handler sees them and closes the panel that just opened.

  So the orb is now purely decorative — pointer-events:none in the CSS —
  and Shopify's own button stays fully clickable underneath at opacity 0.
  The real click lands on Shopify's button, exactly as if the user clicked
  the stock pill. Open, close, click-outside and focus all keep Shopify's
  native behaviour. This file hides the pill, parks the orb on it, and
  restyles the panel surface (see INJECTED_CSS) — it never handles a click.

  Consequences to preserve:
  - The injected CSS hides the pill with opacity ONLY. Adding
    pointer-events:none there makes the pill unclickable and the orb dead,
    because nothing else handles the click.
  - Keyboard and screen-reader users still get Shopify's real button
    (opacity:0 is still focusable and still announced), which is why the
    orb is aria-hidden in the snippet.

  FAIL-SAFE CONTRACT
  The orb ships hidden and is revealed only once the pill has been found
  and measured. If Shopify restructures this, the orb stays hidden and the
  stock pill stays visible and working. Never invert that: a dead chat
  button is worse than an off-brand one.
*/
(function () {
  'use strict';

  var HOST = 'shopify-chat';
  var PILL = '.activator-nudge';
  var STYLE_MARK = 'data-v-chat-hide';

  /* Injected into every shadow root under the chat host. Rules that do not
     apply in a given root simply match nothing, so one stylesheet covers
     all of them.

     WHAT CAN AND CANNOT BE STYLED HERE — verified live, do not re-litigate:

     The chat body is a CROSS-ORIGIN IFRAME (www.veaudry.ae/agent/... — the
     storefront canonical origin is the APEX veaudry.ae, so the www host on
     that src makes it cross-origin even though it looks like our own
     domain; contentDocument is null). Its text, product cards and composer
     pill are unreachable — no CSS, no JS, ever.

     THE "Sign in" BUTTONS ARE NOT IN THAT IFRAME, and are NOT styled here.
     They read as part of the chat body, which is why an earlier version of
     this comment wrote them off as unreachable. They are reachable — they
     sit in their own shadow roots (<shopify-sign-in-button>, <shop-login>).
     But the same popover also mounts OUTSIDE <shopify-chat>, opened from
     the header account icon, and this file only ever walks roots under the
     chat host. Styling them from here left the header copy purple.

     So all sign-in styling lives in assets/shop-login-skin.js, which walks
     the whole document. Do not add sign-in rules back into this file: two
     injectors for one control is how they drift apart.

     But that iframe is TRANSPARENT, so section.panel's background paints
     the entire chat interior. That is the one real lever we have, and it is
     why the surface below is set here rather than left to Shopify.

     THE PANEL MUST STAY LIGHT. The body copy inside the iframe is dark and
     cannot be changed, and Shopify's agent has no dark variant — tested
     with a #14110D panel under prefers-color-scheme:dark and the greeting
     became unreadable dark-on-dark. So this surface stays light in BOTH
     site themes. It reads as a lightbox over the dark pages, which is the
     correct pattern anyway. Do not "fix" this to match [data-theme].

     Design: option A, "ivory hairline" — chosen by the user over a gold
     gradient edge and a black frame. */
  var INJECTED_CSS = [
    /* opacity only. The pill must keep its pointer-events so the real click
       still reaches it through the orb. */
    '.activator-nudge,.activator{opacity:0!important}',

    /* Warm ivory rather than Shopify's 80% white: it takes the clinical
       edge off without touching legibility of text we do not control.
       The gold hairline is an INSET box-shadow, not a border — a border
       would add to the panel's box and shift its layout. */
    'section.panel{' +
      'background:#FAF7F2!important;' +
      'box-shadow:inset 0 0 0 1px rgba(225,160,62,0.55),' +
      '0 24px 60px rgba(20,17,13,0.28)!important;' +
      '}',

    /* Shopify ships rgba(0,0,0,0.2). Deeper, and warm rather than neutral
       black, to sit in the same family as --bg-primary. */
    '.backdrop{background:rgba(20,17,13,0.45)!important}',
  ].join('');

  var GIVE_UP_AFTER_MS = 20000;

  var orb = document.querySelector('[data-v-chat-launcher]');
  if (!orb) return;

  var ready = false;
  var pageObserver = null;
  var timeoutId = null;
  var watched = [];
  var queued = false;
  var observedPill = null;
  var pillResizeObserver = null;

  /* Timestamp until which the orb stays withdrawn regardless of what the
     pill measures. Set on pointerdown so the orb leaves on the press rather
     than waiting for Shopify's pill to collapse, which only happens once
     the panel is already animating in — that lag is what made the chat
     appear to open behind the orb.

     The window is a safety valve, not a guess at the animation length: if
     the press somehow does not open the panel, this expires and the orb
     comes back rather than being stranded off-screen.

     Sized generously on purpose. Measured on the live store, the first open
     of a session takes ~1s from press to panel — the agent runtime has to
     download and mount before Shopify collapses its pill. If the grace
     expires before that, the orb pops back in for a frame and then leaves
     again. Later opens are far quicker; this only has to cover the slowest
     one, and a stuck orb returning 2.5s late is a non-event next to a
     flicker on every first open. */
  var withdrawUntil = 0;
  var WITHDRAW_GRACE_MS = 2500;

  /* ----------------------------------------------------------------
     GETTING OUT OF THE MINI-CART'S WAY

     The orb parks in the same bottom corner the cart drawer slides into,
     where it lands on top of the subtotal row and the free-shipping note.
     Unlike the chat panel, the drawer is ours, so the orb steps aside
     instead of disappearing: it slides by exactly the drawer's width, on
     the drawer's own easing, and lands the same distance from the drawer's
     inner edge as it sat from the viewport edge.

     Measured rather than hard-coded to 50rem, and the side is read from the
     drawer's own computed offsets rather than assumed to be the right:
     cart-drawer.css flips it to left: 0 under [dir=rtl] for the Arabic
     storefront, and Shopify's pill does not necessarily flip with it. Both
     the amount and the direction therefore come from the live boxes.

     CART_DRAWER_OPEN is the class cart-drawer.js toggles on <body>
     (activeOverlayBodyClass). It is the only public signal the drawer
     emits that is readable synchronously; the cartDrawerOpen/Close events
     are also fired, but the class covers the case where the drawer is
     already open at script start. */
  var CART_DRAWER = '.wt-cart__drawer';
  var CART_DRAWER_OPEN = 'page-overlay-cart-on';

  /* Last value written, so the common case — place() re-running on every
     scroll frame with nothing changed — costs one comparison instead of a
     style write that would restart the transition. */
  var appliedShift = 0;

  /* While a slide is in flight the pill's measured x is somewhere between
     its two resting positions, and place() must not treat that halfway
     value as the pill's real home — it would write a left/top from it and
     the orb would jump off the button. Same timestamp-grace pattern as
     withdrawUntil above. Must stay >= the CSS duration. */
  var shiftUntil = 0;
  var SHIFT_MS = 300;

  function applyShift(px) {
    if (px === appliedShift) return;
    appliedShift = px;

    /* On <html>, not on the orb: custom properties inherit across shadow
       boundaries, and both movers — the orb and <shopify-chat> — read this
       one value so they cannot drift apart. */
    document.documentElement.style.setProperty('--v-chat-shift', px + 'px');

    /* Re-measure once the slide has settled, so the orb re-seats on the
       pill's new resting position after the guard window closes. */
    shiftUntil = Date.now() + SHIFT_MS;
    window.setTimeout(schedule, SHIFT_MS + 50);
  }

  function shift() {
    /* No drawer in the DOM on the cart page template, where theme.liquid
       renders the full cart instead of the drawer. Nothing to dodge. */
    var drawer = document.querySelector(CART_DRAWER);
    if (!drawer) return;

    if (!document.body.classList.contains(CART_DRAWER_OPEN)) {
      applyShift(0);
      return;
    }

    /* offsetWidth, not a rect: the drawer is mid-slide when this runs, and
       its rect is wherever the transform has got to this frame. offsetWidth
       ignores transforms and gives the settled width. */
    var width = drawer.offsetWidth;
    var viewport = document.documentElement.clientWidth;
    var size = orb.offsetWidth || 54;

    /* Full-width drawer (the phone layout) — there is no clear space to
       move into. The orb is already faded out there by cart-drawer.css's
       `body.page-overlay-cart-on > *` rule under 600px, so leaving the
       shift at zero avoids animating something nobody can see. */
    if (!width || width + size >= viewport) {
      applyShift(0);
      return;
    }

    /* place() writes style.left; read that rather than a rect, so a shift
       already in flight does not feed back into the next measurement. */
    var left = parseFloat(orb.style.left);
    if (isNaN(left)) left = orb.getBoundingClientRect().x;

    /* Which edge the drawer occupies, per cart-drawer.css: right: 0 by
       default, left: 0 under [dir=rtl]. */
    var onRight = parseFloat(getComputedStyle(drawer).right) === 0;
    var overlaps = onRight ? left + size > viewport - width : left < width;

    if (!overlaps) {
      applyShift(0);
      return;
    }

    applyShift(onRight ? -width : width);
  }

  /* The mirror image, for closing. Measured on the live store: Shopify does
     the entire close in one step ~786ms after the press — panel 1019 -> 0
     and pill 0 -> 85 in the same frame. Waiting for the pill to come back
     means the orb returns almost a second after the user asked for it.

     So the orb is restored on the closing GESTURE — see onOutsidePress,
     which covers every close path, and onClosePress for the X — and this
     timestamp keeps it on screen through the gap where the panel is still
     up and the pill still measures 0x0. Without it, the next refresh would
     read that 0x0 as "chat is open" and hide the orb straight back.

     It is a ceiling, not a wait: once the pill measures non-zero again,
     place() takes over normally. It only has to outlast the teardown. */
  var restoreUntil = 0;
  var RESTORE_GRACE_MS = 1600;
  var observedClose = null;

  /* Every shadow root at or below the chat host. Collected fresh each time,
     because new roots appear when the agent runtime mounts. */
  function shadowRoots(host, acc) {
    if (!host || !host.shadowRoot) return acc;
    var root = host.shadowRoot;
    acc.push(root);

    var all = root.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      if (all[i].shadowRoot) shadowRoots(all[i], acc);
    }
    return acc;
  }

  function chatHost() {
    return document.querySelector(HOST);
  }

  /* Idempotent via the marker attribute, so re-running is cheap. */
  function injectInto(roots) {
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].querySelector('style[' + STYLE_MARK + ']')) continue;
      var style = document.createElement('style');
      style.setAttribute(STYLE_MARK, '');
      style.textContent = INJECTED_CSS;
      roots[i].appendChild(style);
    }
  }

  /* Mutations inside a shadow root do NOT bubble out to an observer on the
     light DOM, so each root needs its own or a rebuilt subtree comes back
     unstyled and the white pill flashes up. */
  function watch(roots) {
    for (var i = 0; i < roots.length; i++) {
      if (watched.indexOf(roots[i]) !== -1) continue;
      watched.push(roots[i]);
      new MutationObserver(schedule).observe(roots[i], {
        childList: true,
        subtree: true,
      });
    }
  }

  function findPill(roots) {
    for (var i = 0; i < roots.length; i++) {
      var pill = roots[i].querySelector(PILL);
      if (pill) return pill;
    }
    return null;
  }

  /* Centre the orb on the pill rather than trusting a fixed corner offset,
     so it tracks Shopify's own responsive positioning instead of drifting
     away from the button the user is actually clicking. */
  function place(pill) {
    /* chat-launcher.css takes the whole chat out (display:none) while the
       mini-cart is open. The pill then measures 0x0, which the check below
       would misread as "panel is open". Leave the orb's state alone; the
       body-class observer re-runs this as soon as the cart closes. */
    if (document.body.classList.contains(CART_DRAWER_OPEN)) return false;

    var box = pill.getBoundingClientRect();

    /* Zero box = panel is open (fact 3 above). Shopify withdraws its own
       pill at this point, and the orb does the same: left on screen it
       sits on top of the open panel and over the message input, which is
       not how a chat launcher behaves. */
    if (!box.width || !box.height) {
      /* Panel is open — unless the user just pressed close, in which case
         hold the orb on screen through Shopify's ~786ms teardown instead of
         hiding it again for the rest of that gap. */
      if (Date.now() >= restoreUntil) {
        orb.classList.add('v-chat-launcher--away');
        /* Mirrored onto <body> for stylesheets that need to know the panel
           is up. The orb's own class cannot serve them: <shopify-chat> is a
           SIBLING of the orb, not a descendant, so nothing scoped to the
           orb can reach the host. chat-launcher.css's over-the-hero rule is
           the one consumer — it hides the host on phones and must not do
           that to an open conversation. */
        document.body.classList.add('v-chat-panel-open');
      }
      return false;
    }

    /* The pill still measures full size in the moment between the press and
       the panel opening. Without this the very next frame would put the orb
       straight back, cancelling the fade-out. */
    if (Date.now() < withdrawUntil) return false;

    orb.classList.remove('v-chat-launcher--away');
    document.body.classList.remove('v-chat-panel-open');

    /* Mid-slide: the pill is somewhere between its two resting positions
       and is not worth measuring. The orb is riding the same transform, so
       it is already tracking the pill exactly — leaving left/top alone is
       what keeps them together. The timeout in applyShift() brings us back
       once the slide has landed. */
    if (Date.now() < shiftUntil) return true;

    var size = orb.getBoundingClientRect().width || 54;

    /* box.x includes the shift, because <shopify-chat> carries the same
       translate as the orb. left/top are the UNSHIFTED home position — the
       transform adds the offset back at paint time. Skipping this
       subtraction would apply the shift twice. */
    var x = box.x - appliedShift;

    orb.style.left = Math.round(x + box.width / 2 - size / 2) + 'px';
    orb.style.top = Math.round(box.y + box.height / 2 - size / 2) + 'px';
    orb.style.right = 'auto';
    orb.style.bottom = 'auto';

    /* After left/top, because shift() reads style.left to decide whether
       the orb is actually in the drawer's corner. */
    shift();
    return true;
  }

  /* Withdraw immediately on press. If the panel opens, the pill collapses
     to 0x0 within the grace window and the orb simply stays away; if it
     does not, the timer below restores it. */
  function onPress() {
    withdrawUntil = Date.now() + WITHDRAW_GRACE_MS;
    orb.classList.add('v-chat-launcher--away');
    window.setTimeout(schedule, WITHDRAW_GRACE_MS + 50);
  }

  function restore() {
    withdrawUntil = 0;
    restoreUntil = Date.now() + RESTORE_GRACE_MS;
    orb.classList.remove('v-chat-launcher--away');
    window.setTimeout(schedule, RESTORE_GRACE_MS + 50);
  }

  function onClosePress() {
    restore();
  }

  function isAway() {
    return orb.classList.contains('v-chat-launcher--away');
  }

  function inBox(box, e) {
    return (
      !!box.width &&
      e.clientX >= box.x &&
      e.clientX <= box.right &&
      e.clientY >= box.y &&
      e.clientY <= box.bottom
    );
  }

  /* ----------------------------------------------------------------
     THE ORB COMES BACK ON THE GESTURE, NOT ON SHOPIFY'S TEARDOWN

     onClosePress above only fires for one element: the X in the panel
     header. Every OTHER way of closing the chat — backdrop click, click
     anywhere on the page, Escape, or pressing the cart trigger — missed it
     and fell through to the slow path, where the orb waits for Shopify's
     pill to measure non-zero again. That is ~786ms of teardown, and worse
     when it lands inside the 2500ms withdraw window: place() then refuses
     to show the orb until that window expires, so the orb sat out the rest
     of the 2.5s. That is the "few seconds" delay, and it was reachable
     three different ways.

     So the restore is driven by the gesture instead. Any press that is
     NOT inside the chat's own UI is a press that closes the chat — that is
     Shopify's own click-outside behaviour, documented at the top of this
     file — so the orb can come back on that press rather than waiting to
     be told.

     Discriminated by GEOMETRY, for the same reason findCloseControl is:
     the panel's contents are slotted across several shadow roots, so a
     containment test on the event target is unreliable, and the chat body
     is a cross-origin iframe whose presses never reach this listener at
     all. Two rectangles are enough:

       inside section.panel  -> the user is USING the chat. Not a close.
                                (The X sits in here too, and is handled by
                                its own listener above, which runs after
                                this one and does the restore.)
       inside .activator-nudge -> the press that OPENS the chat. onPress
                                owns that; restoring here would cancel the
                                withdraw it just started.
       anything else         -> closes the chat. Restore now.

     Capture phase, so this is settled before Shopify's own handlers run,
     and it only does the shadow-root walk while the orb is actually away,
     which is the only time there is anything to restore. */
  function onOutsidePress(e) {
    if (!isAway()) return;

    var roots = shadowRoots(chatHost(), []);
    var i;

    for (i = 0; i < roots.length; i++) {
      var panel = roots[i].querySelector('section.panel');
      if (panel && inBox(panel.getBoundingClientRect(), e)) return;
    }

    var pill = findPill(roots);
    if (pill && inBox(pill.getBoundingClientRect(), e)) return;

    restore();
  }

  /* Escape closes the panel too, and is not a pointer event. No geometry
     needed — if the orb is away, Escape can only mean "close the chat". */
  function onEscape(e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    if (!isAway()) return;
    restore();
  }

  /* The rightmost control in the panel header is the close/minimise button.

     Found by geometry, for two separate reasons:

     - Not by aria-label="Close chat": the store ships an Arabic locale and
       Shopify localises that label, so a string match would quietly stop
       working on the Arabic storefront.
     - Not by querying inside .panel__header-column--right either: that
       column is in one shadow root while the button is slotted in from
       ANOTHER, several roots deeper, so a descendant query on the column
       returns nothing. Verified live — this is the trap that made the first
       version of this function always return null.

     So: measure the column's box, then take the rightmost visible button
     anywhere under the chat host whose centre falls inside it. */
  function findCloseControl(roots) {
    var column = null;

    for (var i = 0; i < roots.length; i++) {
      var node = roots[i].querySelector('.panel__header-column--right');
      if (!node) continue;
      var columnBox = node.getBoundingClientRect();
      if (columnBox.width) {
        column = columnBox;
        break;
      }
    }
    if (!column) return null;

    var best = null;
    var bestX = -Infinity;

    for (var r = 0; r < roots.length; r++) {
      var buttons = roots[r].querySelectorAll('button');

      for (var j = 0; j < buttons.length; j++) {
        var box = buttons[j].getBoundingClientRect();
        if (!box.width) continue;

        var centreX = box.x + box.width / 2;
        var centreY = box.y + box.height / 2;
        var inside =
          centreX >= column.x - 2 &&
          centreX <= column.right + 2 &&
          centreY >= column.y - 2 &&
          centreY <= column.bottom + 2;

        if (inside && box.x > bestX) {
          bestX = box.x;
          best = buttons[j];
        }
      }
    }
    return best;
  }

  function refresh() {
    queued = false;

    var host = chatHost();
    if (!host || !host.shadowRoot) return false;

    var roots = shadowRoots(host, []);
    injectInto(roots);
    watch(roots);

    var pill = findPill(roots);
    if (!pill) return false;

    applyCornerBlock(pill);

    /* The open/close transition is a SIZE change on the pill (85x46 <-> 0x0),
       driven by Shopify's own styles. The MutationObserver above watches
       childList only, so it does not see it, and without this the orb would
       stay away after the panel closed. Re-observed whenever the node is
       replaced, which happens on the first open. */
    if (pill !== observedPill) {
      if (pillResizeObserver) pillResizeObserver.disconnect();
      if (window.ResizeObserver) {
        pillResizeObserver = new ResizeObserver(schedule);
        pillResizeObserver.observe(pill);
      }

      /* pointerdown, not click: it fires at the start of the press, so the
         orb is already fading while Shopify is still opening the panel.
         Listening on Shopify's own pill because the orb itself is
         pointer-events:none and never sees the interaction. */
      pill.addEventListener('pointerdown', onPress);
      observedPill = pill;
    }

    /* Rebound every time the panel mounts, since the header is torn down
       with it. */
    var closeControl = findCloseControl(roots);
    if (closeControl && closeControl !== observedClose) {
      closeControl.addEventListener('pointerdown', onClosePress);
      observedClose = closeControl;
    }

    place(pill);
    return true;
  }

  /* ----------------------------------------------------------------
     Blocking the chat where the hero's own controls live.

     On phones the hero is a full screen tall and its chapter arrows and
     thumb bubble sit in this same bottom-right corner, so for that first
     screen the chat has to be untappable, not merely invisible.

     chat-launcher.css hides both the orb and the <shopify-chat> host over
     that span, and that alone is nearly enough — but only nearly. The host
     is the only part of the chat a theme stylesheet can reach; the pill
     itself is two shadow roots down, and a descendant is free to set
     pointer-events:auto and win against the host's :none. Rather than bet
     the fix on Shopify never doing that, the pill is blocked directly
     here: an inline style on the real element, which no selector has to
     cross a shadow boundary to apply and nothing in their CSS outranks.

     Kept deliberately narrow. Only pointer-events, because the pill is
     already at opacity 0 (INJECTED_CSS) and the orb above it is what the
     customer actually sees. Clearing it back to '' rather than 'auto'
     hands the pill back to Shopify's own styles untouched.
     ---------------------------------------------------------------- */
  var BLOCK_MAX_WIDTH = 749;

  function cornerBlocked() {
    var body = document.body;
    return (
      window.innerWidth <= BLOCK_MAX_WIDTH &&
      body.classList.contains('product-hero-corner-busy') &&
      /* An open conversation is never blocked — scrolling back up to the
         hero mid-chat must not make the panel untappable. */
      !body.classList.contains('v-chat-panel-open')
    );
  }

  function applyCornerBlock(pill) {
    var want = cornerBlocked() ? 'none' : '';
    if (pill.style.pointerEvents !== want) pill.style.pointerEvents = want;
  }

  /* The hero toggles its class from a scroll handler, and a class change on
     <body> is not something any of the observers above would see — they all
     watch inside the chat. attributeFilter keeps this to the one attribute
     that matters, and schedule() coalesces to a frame like everything else. */
  if (window.MutationObserver) {
    new MutationObserver(schedule).observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  /* Crossing the phone breakpoint changes the answer without any class
     changing, so the block has to be re-evaluated on resize too. */
  window.addEventListener('resize', schedule, { passive: true });

  /* The walk touches every node under the chat host and the runtime mutates
     in bursts while mounting, so coalesce to one pass per frame. */
  function schedule() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(refresh);
  }

  function activate() {
    if (!refresh()) return false;

    ready = true;

    /* Unhidden for good — [hidden] is the "we never found the chat" state
       and is never re-applied. Visibility from here on is the --away class,
       which tracks whether the panel is open. */
    orb.hidden = false;

    /* Re-measure on the things that move a fixed element. Scroll matters
       because the pill's viewport position can shift with mobile browser
       chrome collapsing. */
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('scroll', schedule, { passive: true });

    /* The mini-cart opening is a class flip on <body>, not a DOM change
       under the chat host, so none of the observers above see it. Watching
       the attribute directly also covers every way the drawer can open —
       cart trigger, add-to-cart, quick-add, back-link — without this file
       having to know about any of them.

       shift() directly rather than schedule(): refresh() re-walks every
       shadow root under the chat host, which is far more work than a class
       flip warrants, and it would fight the in-flight slide by re-running
       place() mid-animation. */
    new MutationObserver(shift).observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });

    /* Covers the drawer already being open when this script initialises —
       a slow chat mount behind a fast add-to-cart. */
    shift();

    /* See onOutsidePress: brings the orb back on the closing gesture
       itself, for every close path except the X (which has its own
       listener). Capture phase so it is decided before Shopify's own
       click-outside handler acts on the same press. */
    document.addEventListener('pointerdown', onOutsidePress, true);
    document.addEventListener('keydown', onEscape, true);

    /* Debug handle for a live storefront: __vChat.refresh() re-injects and
       re-measures, __vChat.pill() returns the node being tracked. */
    window.__vChat = {
      refresh: refresh,
      pill: function () {
        var host = chatHost();
        return host ? findPill(shadowRoots(host, [])) : null;
      },
    };

    return true;
  }

  function attempt() {
    if (ready) return true;
    if (!activate()) return false;
    if (pageObserver) pageObserver.disconnect();
    if (timeoutId) clearTimeout(timeoutId);
    return true;
  }

  if (attempt()) return;

  /* <shopify-chat> is injected asynchronously through content_for_header
     and is routinely absent at parse time. */
  pageObserver = new MutationObserver(attempt);
  pageObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  timeoutId = setTimeout(function () {
    if (pageObserver) pageObserver.disconnect();
    if (!ready) {
      console.warn(
        '[veaudry] shopify-chat not found; keeping the stock chat button.'
      );
    }
  }, GIVE_UP_AFTER_MS);
})();
